import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  CalendarDays, AlertTriangle, ChevronRight, Loader2, Ticket, DollarSign,
  ListChecks, Users, FileText, Wrench, Award, Megaphone, ExternalLink, Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmt2 = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CrossPlatformPanel({ token, section, data: providedData, externallyManaged }) {
  const navigate = useNavigate();
  const [data, setData] = useState(providedData || null);
  const [loading, setLoading] = useState(!providedData);

  const load = useCallback(async () => {
    if (externallyManaged) return; // parent owns the fetch; never refetch here
    if (providedData) return;
    try {
      const res = await axios.get(`${API}/admin/dashboard/cross-platform`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [token, providedData, externallyManaged]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (providedData) { setData(providedData); setLoading(false); } }, [providedData]);

  if (loading) {
    return <div className="py-6 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-500" /></div>;
  }
  if (!data) return null;

  const { events_pulse: pulse, tasks, departments: deps } = data;

  // Sub-section: Events Pulse strip
  const eventsPulseSection = (
      <Card className="border-border bg-gradient-to-br from-indigo-500/5 to-emerald-500/5">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <CalendarDays className="h-4 w-4 text-indigo-400" />
            <h3 className="text-sm font-semibold">Events Pulse</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <PulseTile
              testid="pulse-today"
              icon={CalendarDays}
              color="indigo"
              label="Today's events"
              value={pulse.today_count}
              cta="View"
              onClick={() => navigate('/admin/events')}
            />
            <PulseTile
              testid="pulse-tickets-today"
              icon={Ticket}
              color="emerald"
              label="Tickets sold today"
              value={pulse.tickets_sold_today}
              sub={`${pulse.orders_today} order${pulse.orders_today === 1 ? '' : 's'}`}
            />
            <PulseTile
              testid="pulse-revenue-today"
              icon={DollarSign}
              color="amber"
              label="Revenue today"
              value={fmt(pulse.revenue_today_cents)}
            />
            <PulseTile
              testid="pulse-upcoming"
              icon={Clock}
              color="rose"
              label="Upcoming events"
              value={pulse.upcoming_count}
            />
          </div>

          {/* Today's events list */}
          {pulse.today_events?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border space-y-1.5" data-testid="pulse-today-events">
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Today on the calendar</p>
              {pulse.today_events.map(ev => (
                <div key={ev.event_id} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-medium truncate">{ev.name}</span>
                    {ev.event_time && <span className="text-xs text-muted-foreground">{ev.event_time}</span>}
                    {ev.venue_name && <span className="text-xs text-muted-foreground truncate">· {ev.venue_name}</span>}
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => window.open(`/events/${ev.city}/${ev.slug}`, '_blank')}>
                    Open <ExternalLink className="ml-1 h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
  );

  // Sub-section: Tasks Across All Events
  const tasksSection = (
      <Card className="border-border" data-testid="tasks-across-events">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-amber-400" />
              <h3 className="text-sm font-semibold">Tasks across all events</h3>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px]" data-testid="tasks-counts">
                {tasks.counts.overdue} overdue · {tasks.counts.due_this_week} this week · {tasks.counts.in_progress} in progress
              </Badge>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <TaskList
              testid="overdue-list"
              title="Overdue"
              accent="text-rose-400"
              icon={AlertTriangle}
              tasks={tasks.overdue}
              empty="Nothing overdue. Nice."
              onJump={(t) => navigate(`/admin/events?event=${t.event_id}&task=${t.task_id}`)}
            />
            <TaskList
              testid="due-week-list"
              title="Due this week"
              accent="text-amber-400"
              icon={Clock}
              tasks={tasks.due_this_week}
              empty="Light week ahead."
              onJump={(t) => navigate(`/admin/events?event=${t.event_id}&task=${t.task_id}`)}
            />
          </div>
        </CardContent>
      </Card>
  );

  // Sub-section: Departments At-a-Glance
  const departmentsSection = (
      <div data-testid="departments-grid">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">Departments at a glance</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <DeptCard
            testid="dept-crm"
            icon={Users}
            color="indigo"
            title="CRM"
            primary={deps.crm.new_leads_week}
            primaryLabel="new leads this week"
            stats={[
              { label: 'Open', value: deps.crm.open_total },
              { label: 'Contacted', value: deps.crm.contacted },
            ]}
            cta="Open CRM"
            onClick={() => navigate('/admin/crm')}
          />
          <DeptCard
            testid="dept-proposals"
            icon={FileText}
            color="emerald"
            title="Proposals & Invoices"
            primary={deps.proposals.invoices_unpaid}
            primaryLabel="invoices unpaid"
            stats={[
              { label: 'Drafts', value: deps.proposals.drafts },
              { label: 'Sent', value: deps.proposals.sent },
              { label: 'Accepted', value: deps.proposals.accepted },
            ]}
            cta="Open pipeline"
            onClick={() => navigate('/admin/pipeline')}
          />
          <DeptCard
            testid="dept-staffing"
            icon={Users}
            color="amber"
            title="Staffing"
            primary={deps.staffing.open_jobs}
            primaryLabel="open jobs"
            stats={[
              { label: 'Filled', value: deps.staffing.filled_jobs },
              { label: 'Pending applicants', value: deps.staffing.pending_applicants },
            ]}
            cta="Open staffing"
            onClick={() => window.open('/staffing', '_blank')}
          />
          <DeptCard
            testid="dept-sponsorships"
            icon={Award}
            color="rose"
            title="Sponsorships"
            primary={fmt(deps.sponsorships.revenue_week_cents)}
            primaryLabel={`${deps.sponsorships.paid_this_week} paid this week`}
            stats={[
              { label: 'Paid total', value: deps.sponsorships.paid_total },
            ]}
            cta="Manage events"
            onClick={() => navigate('/admin/events')}
          />
          <DeptCard
            testid="dept-tickets"
            icon={Ticket}
            color="indigo"
            title="Tickets"
            primary={fmt(deps.tickets.revenue_week_cents)}
            primaryLabel={`${deps.tickets.tickets_week} sold this week`}
            stats={
              deps.tickets.top_event
                ? [{ label: `Top: ${deps.tickets.top_event.name.slice(0, 22)}${deps.tickets.top_event.name.length > 22 ? '…' : ''}`, value: `${deps.tickets.top_event.tickets} · ${fmt2(deps.tickets.top_event.revenue_cents)}` }]
                : [{ label: 'Top event', value: 'No sales yet' }]
            }
            cta="Ticket holders"
            onClick={() => navigate('/admin/ticket-holders')}
          />
          <DeptCard
            testid="dept-production"
            icon={Wrench}
            color="emerald"
            title="Production"
            primary={deps.production.active_tasks}
            primaryLabel="active tasks"
            stats={[
              { label: 'Vendors confirmed', value: deps.production.vendors_confirmed },
              { label: 'Vendors pending', value: deps.production.vendors_pending },
            ]}
            cta="Open events"
            onClick={() => navigate('/admin/events')}
          />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
          <QuickLink testid="ql-marketing" icon={Megaphone} label="Marketing" onClick={() => navigate('/admin/marketing')} />
          <QuickLink testid="ql-rentals" icon={Wrench} label="Rentals" onClick={() => navigate('/admin/rentals')} />
          <QuickLink testid="ql-blog" icon={FileText} label="Blog" onClick={() => navigate('/admin/blog')} />
          <QuickLink testid="ql-promo" icon={Award} label="Promo codes" onClick={() => navigate('/admin/promo-codes')} />
        </div>
      </div>
  );

  if (section === 'events_pulse') return eventsPulseSection;
  if (section === 'tasks_across_events') return tasksSection;
  if (section === 'departments') return departmentsSection;

  // Default: legacy combined render (backward compat).
  return (
    <div className="space-y-6 mb-6" data-testid="cross-platform-panel">
      {eventsPulseSection}
      {tasksSection}
      {departmentsSection}
    </div>
  );
}

const COLOR = {
  indigo: { bg: 'bg-indigo-500/10', text: 'text-indigo-400' },
  emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-400' },
  amber: { bg: 'bg-amber-500/10', text: 'text-amber-400' },
  rose: { bg: 'bg-rose-500/10', text: 'text-rose-400' },
};

function PulseTile({ icon: Icon, color = 'indigo', label, value, sub, onClick, cta, testid }) {
  const c = COLOR[color];
  return (
    <div className="rounded-lg border border-border bg-card p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <div className={`p-1.5 rounded ${c.bg}`}><Icon className={`h-3.5 w-3.5 ${c.text}`} /></div>
        {onClick && cta && (
          <button onClick={onClick} className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-0.5">
            {cta} <ChevronRight className="h-3 w-3" />
          </button>
        )}
      </div>
      <p className="text-xl font-bold tabular-nums">{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

function TaskList({ title, accent, icon: Icon, tasks, empty, onJump, testid }) {
  return (
    <div data-testid={testid}>
      <p className={`text-[11px] font-semibold uppercase tracking-wide flex items-center gap-1 mb-2 ${accent}`}>
        <Icon className="h-3 w-3" /> {title} ({tasks.length})
      </p>
      {tasks.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2">{empty}</p>
      ) : (
        <div className="space-y-1.5 max-h-[220px] overflow-y-auto pr-1">
          {tasks.map(t => (
            <button
              key={t.task_id}
              onClick={() => onJump(t)}
              className="w-full text-left p-2.5 rounded-lg border border-border hover:border-indigo-500/40 hover:bg-indigo-500/5 transition-colors"
              data-testid={`task-${t.task_id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium truncate">{t.name}</span>
                {t.due_date && <span className="text-[10px] text-muted-foreground flex-shrink-0">due {t.due_date}</span>}
              </div>
              <div className="flex items-center gap-2 text-[10px] text-muted-foreground mt-0.5">
                <span className="truncate">{t.event_name}</span>
                {t.responsible && <span>· {t.responsible}</span>}
                <Badge variant="outline" className="text-[9px] capitalize">{(t.status || '').replace('_', ' ')}</Badge>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DeptCard({ icon: Icon, color = 'indigo', title, primary, primaryLabel, stats = [], cta, onClick, testid }) {
  const c = COLOR[color];
  return (
    <Card className="border-border" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded ${c.bg}`}><Icon className={`h-4 w-4 ${c.text}`} /></div>
            <h4 className="text-sm font-semibold">{title}</h4>
          </div>
        </div>
        <div className="mb-3">
          <p className="text-2xl font-bold tabular-nums">{primary}</p>
          <p className="text-[11px] text-muted-foreground">{primaryLabel}</p>
        </div>
        <div className="space-y-1">
          {stats.map((s, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground truncate pr-2">{s.label}</span>
              <span className="font-medium tabular-nums whitespace-nowrap">{s.value}</span>
            </div>
          ))}
        </div>
        {cta && (
          <Button variant="outline" size="sm" onClick={onClick} className="w-full mt-3 h-7 text-xs" data-testid={`${testid}-cta`}>
            {cta} <ChevronRight className="ml-1 h-3 w-3" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function QuickLink({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="border border-border rounded-lg p-2.5 text-xs hover:border-indigo-500/40 hover:bg-indigo-500/5 transition-colors flex items-center gap-2"
      data-testid={testid}
    >
      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      <span className="font-medium">{label}</span>
      <ChevronRight className="h-3 w-3 text-muted-foreground ml-auto" />
    </button>
  );
}
